import { useId, useState } from "react";
import Popover from "@mui/material/Popover";
import { Check, Info } from "lucide-react";
import { TOUCH_SQUARE } from "@/components/buttonStyles";
import type { ProgramInfo } from "@/lib/programEnrollment";

// BRK-S401 — the "i" beside a program's chip on the Current program enrollment
// block: what the program is, and — only when the insured holds it — its own
// limits and rate (`programInfo`). A dialog-like popover, so it closes on
// Escape and on a click away, and its trigger says what it opens.
export function ProgramInfoPopover({ info }: { info: ProgramInfo }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const titleId = useId();
  const panelId = useId();
  const open = anchor !== null;

  return (
    <>
      <button
        type="button"
        aria-label={`About ${info.title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-brk-dim transition-colors hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
      >
        <Info size={16} aria-hidden />
      </button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Never wider than a 375px phone less its gutters.
        slotProps={{
          paper: { sx: { maxWidth: "min(20rem, calc(100vw - 32px))" } },
        }}
      >
        <div
          id={panelId}
          role="dialog"
          aria-labelledby={titleId}
          className="flex flex-col gap-3 p-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <p id={titleId} className="text-subheading text-brk-text">
              {info.title}
            </p>
            <span className="rounded-full bg-brk-bg px-2 py-0.5 text-caption font-medium text-brk-dim">
              {info.tag}
            </span>
          </div>
          {info.stats.length > 0 && (
            <dl className="grid grid-cols-1 gap-2">
              {info.stats.map((s) => (
                <div key={s.label} className="flex flex-col">
                  <dt className="text-caption text-brk-dim">{s.label}</dt>
                  <dd className="text-body font-semibold text-brk-text">
                    {s.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <ul className="flex flex-col gap-1.5">
            {info.points.map((p) => (
              <li key={p} className="flex gap-2 text-meta text-brk-text">
                <Check
                  size={14}
                  aria-hidden
                  className="mt-0.5 shrink-0 text-brk-accent"
                />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </Popover>
    </>
  );
}
